import ExcelJS from "exceljs/dist/exceljs.min.js";
import html2canvas from "html2canvas";
import { pdfCell } from "./downloadReportPdf";
import { piePng } from "./reportPieImage";

const TITLE = "FF1B2559";
const TEXT = "FF374151";
const HEADER = "FF42AACC";
const WHITE = "FFFFFFFF";
const BAND = "FFF4F7FE";

function fileName(view) {
  return String(view?.title || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "report";
}

function sheetName(view) {
  const name = String(view?.title || "Report").replace(/[\\/*?:[\]]/g, " ").trim() || "Report";
  return name.slice(0, 31);
}

function paint(cell, { bold = false, size = 10, color = TITLE, fill = "" } = {}) {
  cell.font = { name: "Calibri", bold, size, color: { argb: color } };
  cell.alignment = { vertical: "middle", wrapText: true };
  if (fill) {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
  }
}

async function saveWorkbook(workbook, name) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob(
    [buffer],
    { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

function workbookFor(name) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName({ title: name }), {
    pageSetup: {
      orientation: "landscape",
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
  });
  return { workbook, sheet };
}

export async function downloadReportViewExcel(view, fetchedAt) {
  const fetchedLabel = fetchedAt || view?.generatedAt || new Date().toLocaleString();
  const span = Math.max(
    3,
    ...(view?.tables || []).map((table) => (table.columns || []).length)
  );
  const { workbook, sheet } = workbookFor(view?.title || "Report");
  sheet.pageSetup.orientation = "landscape";
  for (let column = 1; column <= span; column += 1) {
    sheet.getColumn(column).width = column === span ? 36 : 22;
  }
  let row = 1;
  const merge = (text, style) => {
    const cell = sheet.getCell(row, 1);
    cell.value = text;
    paint(cell, style);
    if (span > 1) sheet.mergeCells(row, 1, row, span);
    row += 1;
  };

  merge(view?.title || "Report", { bold: true, size: 16 });
  merge(`Fetched: ${fetchedLabel}`, { size: 11, color: TEXT });
  if (view?.summary) merge(view.summary, { size: 11, color: TEXT });
  row += 1;

  const placePie = (slices) => {
    if (!slices.length) return;
    const image = workbook.addImage({
      base64: piePng(slices).split(",")[1],
      extension: "png",
    });
    sheet.addImage(image, {
      tl: { col: 0, row: row - 1 },
      ext: { width: 480, height: 280 },
    });
    row += 16;
  };

  const writeTable = (headers, rows) => {
    headers.forEach((header, index) => {
      const cell = sheet.getCell(row, index + 1);
      cell.value = String(header);
      paint(cell, { bold: true, size: 10, color: WHITE, fill: HEADER });
    });
    sheet.getRow(row).height = 18;
    row += 1;
    if (rows.length === 0) {
      const cell = sheet.getCell(row, 1);
      cell.value = "No rows.";
      paint(cell);
      row += 2;
      return;
    }
    rows.forEach((values, rowIndex) => {
      values.forEach((value, index) => {
        const cell = sheet.getCell(row, index + 1);
        cell.value = value == null ? "" : String(value);
        paint(cell, { fill: rowIndex % 2 === 0 ? BAND : "" });
      });
      row += 1;
    });
    row += 1;
  };

  (view?.charts || []).forEach((chart) => {
    merge(chart.title || "Chart", { bold: true, size: 13 });
    const slices = chart.slices || [];
    placePie(slices);
    writeTable(
      ["Item", "Count", "Detail"],
      slices.map((slice) => [slice.label || "", String(slice.value ?? 0), slice.detail || ""])
    );
  });

  (view?.tables || []).forEach((dataTable) => {
    const columns = dataTable.columns || [];
    if (columns.length === 0) return;
    merge(dataTable.title || "Table", { bold: true, size: 13 });
    if (dataTable.subtitle) merge(dataTable.subtitle, { size: 11, color: TEXT });
    writeTable(
      columns.map((column) => column.label || column.key),
      (dataTable.rows || []).map((record) => columns.map((column) => pdfCell(record[column.key], column)))
    );
  });

  sheet.headerFooter.oddHeader = `&RFetched: ${fetchedLabel}`;
  await saveWorkbook(workbook, fileName(view));
}

export async function downloadVisibleReportExcel(element, fetchedAt) {
  const canvas = await html2canvas(element, { scale: 2, useCORS: true });
  const { workbook, sheet } = workbookFor("Report");
  const fetchedLabel = fetchedAt || new Date().toLocaleString();
  const cell = sheet.getCell(1, 1);
  cell.value = `Fetched: ${fetchedLabel}`;
  paint(cell, { size: 11, color: TEXT });
  const image = workbook.addImage({
    base64: canvas.toDataURL("image/png").split(",")[1],
    extension: "png",
  });
  const width = 720;
  const height = Math.max(200, Math.round((canvas.height * width) / canvas.width));
  sheet.addImage(image, {
    tl: { col: 0, row: 1 },
    ext: { width, height },
  });
  sheet.headerFooter.oddHeader = `&RFetched: ${fetchedLabel}`;
  await saveWorkbook(workbook, "report");
}
